import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api, ApiError, type Org } from '../../lib/api';
import { useRefreshSession } from '../../lib/session';
import { Avatar, Spinner, toast } from '@vatman/ui';
import { errorText } from '../../i18n';
import '../../ui/agentActivity.css';

/** Someone from outside with a role in the organization's projects or boards (crates/server/src/api/orgs.rs). */
interface Guest {
  id: string;
  name: string;
  email: string;
  color: string;
  projects: { id: string; key: string; name: string; role: string }[];
  boards: { id: string; title: string; role: string }[];
}

const report = (e: unknown) => toast(errorText(e instanceof ApiError ? e.code : 'internal'));

/**
 * Organization settings → Guests (admins): whether people from outside may be invited at all,
 * who is in and where, and taking someone out of everything at once.
 */
export function OrgGuests({ org }: { org: Org }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const refresh = useRefreshSession();
  const key = ['org-guests', org.id];
  const guests = useQuery({ queryKey: key, queryFn: () => api.get<Guest[]>(`/orgs/${org.id}/guests`) });
  const toggle = async (on: boolean) => {
    try {
      await api.patch(`/orgs/${org.id}`, { guestsAllowed: on });
      await refresh();
      toast(t(on ? 'guests.turnedOn' : 'guests.turnedOff'));
    } catch (e) {
      report(e);
    }
  };
  const remove = async (g: Guest) => {
    try {
      await api.del(`/orgs/${org.id}/guests/${g.id}`);
      toast(t('guests.removed', { name: g.name }));
      void qc.invalidateQueries({ queryKey: key });
    } catch (e) {
      report(e);
    }
  };
  return (
    <div className="settings-card">
      <h3>{t('guests.title')}</h3>
      <label className="agent-switch" style={{ margin: '10px 0 14px' }}>
        <input type="checkbox" checked={org.guestsAllowed} onChange={(e) => void toggle(e.target.checked)} />
        <span>
          <div style={{ fontWeight: 600 }}>{t('guests.allow')}</div>
          <div className="muted" style={{ fontSize: 'var(--fs-3)' }}>
            {t('guests.hint')}
          </div>
        </span>
      </label>
      {guests.isLoading && <Spinner />}
      {guests.data?.length === 0 && <p className="muted">{t('guests.empty')}</p>}
      {guests.data?.map((g) => (
        <div className="person-row" key={g.id}>
          <Avatar name={g.name} color={g.color} size={28} />
          <div className="who">
            <div style={{ fontWeight: 600 }}>{g.name}</div>
            <div className="subtle" style={{ fontSize: 'var(--fs-2)' }}>
              {g.email}
            </div>
            <div className="subtle guest-places" style={{ fontSize: 'var(--fs-2)' }}>
              {g.projects.map((p) => (
                <Link key={p.id} to={`/t/${p.id}`}>
                  {p.key} · {p.name}
                </Link>
              ))}
              {g.boards.map((b) => (
                <Link key={b.id} to={`/b/${b.id}`}>
                  {b.title}
                </Link>
              ))}
            </div>
          </div>
          <button className="btn sm danger" onClick={() => void remove(g)}>
            {t('guests.remove')}
          </button>
        </div>
      ))}
    </div>
  );
}
